import React from "react";
import {useQuery} from "react-apollo";
import get from "lodash/get";
import noop from "lodash/noop";
import classNames from "classnames";
import PropTypes from "prop-types";

import {Text} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {CHAT_SERVICE_FLOW_ENABLED} from "shared/constants";
import {GetChatConversationInboxQuery} from "shared/Inbox/queries/GetChatConversationInbox";
import {ErrorDisplay} from "shared/General/components/ErrorDisplay/ErrorDisplay";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";

import {InboxEmpty} from "../InboxEmpty/InboxEmpty";
import InboxItem from "../InboxItem/InboxItem";
import s from "./InboxUI.pcss";

const InboxUI = (props) => {
    const {id} = props.university;
    const {reportError} = useErrorReporting();

    const isChatServiceEnabled = useBooleanFeatureFlag(
        CHAT_SERVICE_FLOW_ENABLED.key,
        CHAT_SERVICE_FLOW_ENABLED.value,
    );

    const {loading, data, error} = useQuery(GetChatConversationInboxQuery, {
        variables: {
            chatConversationInboxInputDto: {
                product: "widget",
                universityId: id,
            },
        },
        skip: !isChatServiceEnabled,
        fetchPolicy: "cache-and-network",
    });

    if (loading) {
        return <Text>Loading...</Text>;
    }

    let chatGroupToConversationMap;
    // Create map to allow quick access to conversation IDs based on chat group IDs
    if (isChatServiceEnabled && data && !error && !loading) {
        chatGroupToConversationMap = new Map();
        props.chatGroups.map((chatGroup) => {
            const correspondingConversation = data?.getChatConversationInbox?.filter(
                (conversation) => {
                    return conversation?.sourceId === chatGroup?.id;
                },
            )[0];
            if (correspondingConversation) {
                chatGroupToConversationMap.set(chatGroup?.id, correspondingConversation?.id);
            } else {
                reportError(
                    new Error(
                        `No conversation from chat-service found for chat group ${chatGroup.id} in university ${id}`,
                    ),
                );
            }

            return chatGroup;
        });
    }

    const chatGroups = props.chatGroups.map((chatGroup) => (
        <InboxItem
            key={chatGroup.id}
            id={chatGroup.id}
            dataTestId={"inbox-item-id"}
            creationSource={chatGroup.creationSource}
            message={chatGroup.lastMessage}
            unread={chatGroup.unreadMessageCount}
            user={chatGroup.opponent}
            onClick={() => {
                if (isChatServiceEnabled && chatGroupToConversationMap?.has(chatGroup.id)) {
                    props.onOpenChat(chatGroupToConversationMap.get(chatGroup.id));
                } else {
                    props.onOpenChat(chatGroup.id);
                }
            }}
            showOnlineStatus={props.showOnlineStatus}
            university={props.university}
            isExternal={props.isExternal}
        />
    ));
    const universityChatGroup = props.universityChatGroup ? (
        <InboxItem
            key="university-chat"
            id={get(props, "universityChatGroup.id")}
            message={get(props.universityChatGroup, "message")}
            unread={get(props.universityChatGroup, "unreadMessageCount", 0)}
            user={get(props, "universityChatGroup.user")}
            onClick={() => props.onOpenUniversityChat(get(props, "universityChatGroup.id"))}
            showOnlineStatus={props.showOnlineStatus}
        />
    ) : null;

    const chatGroupsExist = props.chatGroups.length !== 0 || props.universityChatGroup;
    return (
        <ErrorDisplay error={error}>
            <div className={classNames(s.container, props.className)}>
                {!props.loading && !chatGroupsExist ? <InboxEmpty /> : null}
                {universityChatGroup}
                {chatGroups}
            </div>
        </ErrorDisplay>
    );
};

InboxUI.propTypes = {
    loading: PropTypes.bool,
    chatGroups: PropTypes.array.isRequired,
    universityChatGroup: PropTypes.shape({
        message: PropTypes.object.isRequired,
        unreadMessageCount: PropTypes.object.isRequired,
        user: PropTypes.shape({
            firstName: PropTypes.string.isRequired,
            profilePhoto: PropTypes.string.isRequired,
        }).isRequired,
    }),
    showOnlineStatus: PropTypes.bool,
    // eslint-disable-next-line react/no-unused-prop-types
    onOpenChat: PropTypes.func.isRequired,
    onOpenUniversityChat: PropTypes.func,
    university: PropTypes.object,
    isExternal: PropTypes.bool,
};

InboxUI.defaultProps = {
    loading: false,
    showOnlineStatus: true,
    universityChatGroup: null,
    onOpenUniversityChat: noop,
    university: {},
    isExternal: false,
};

export default InboxUI;
